<template>
  <tr>
    <td class="shrink">
      <ItemId :item-id="version.id" />
    </td>
    <td>
      {{ version.version }}
      <span
        class="tags"
        v-if="version.tag || version.branch"
      >
        <span
          v-if="version.tag"
          class="tag icon-text"
          :title="`Tagged as ${version.tag}`"
        >
          <span class="icon">
            <i class="icon-tags"></i>
          </span>
          {{ truncateShort(version.tag) }}
        </span>
        <span
          v-if="version.branch"
          class="tag icon-text"
          :title="`On branch ${version.branch}`"
        >
          <span class="icon">
            <i class="icon-git"></i>
          </span>
          {{ truncateShort(version.branch) }}
        </span>
      </span>
      <template v-if="!version.version && !version.tag && !version.branch">—</template>
    </td>
    <td>
      <span
        v-if="version.state"
        class="tag is-capitalized"
        :class="WORKER_VERSION_STATE_COLORS[version.state]"
      >
        {{ version.state }}
      </span>
    </td>
    <td>
      <DockerImageIID
        v-if="version.docker_image_iid"
        :iid="version.docker_image_iid"
      />
      <span v-else>—</span>
    </td>
    <td
      :title="version.created"
      class="shrink"
    >
      {{ ago(new Date(version.created)) }}
    </td>
    <td class="shrink">
      <div class="field has-addons is-justify-content-flex-end">
        <div class="control">
          <router-link
            :to="{ name: 'worker-version', params: { versionId: version.id } }"
            :target="selectFunction ? '_blank' : ''"
            class="button is-small"
            title="View details for this version"
          >
            <i class="icon-eye"></i>
          </router-link>
        </div>
        <div
          class="control"
          v-if="version.revision_url"
        >
          <a
            title="View revision"
            class="button is-small"
            :href="version.revision_url"
            target="_blank"
          >
            <i class="icon-git"></i>
          </a>
        </div>
        <div
          class="control"
          v-if="selectFunction"
        >
          <!-- Allows to select a worker version -->
          <a
            class="button is-success is-small"
            :disabled="!isSelectable || null"
            v-on:click="select"
            :class="{ 'is-loading': loading }"
            :title="selectTitle"
          >
            <i class="icon-plus"></i>
          </a>
        </div>
      </div>
    </td>
  </tr>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'

import ItemId from '@/components/ItemId.vue'
import { WORKER_VERSION_STATE_COLORS } from '@/config'
import { ago, errorParser } from '@/helpers'
import { truncateShort } from '@/mixins/truncate'
import { useNotificationStore } from '@/stores'
import type { WorkerVersion } from '@/types/worker'

import DockerImageIID from './DockerImageIID.vue'

const {
  version,
  onlySelectAvailable = false,
  selectFunction,
} = defineProps<{
  version: WorkerVersion
  /**
   * A function called when this WorkerVersion is selected.
   * The button to select a worker version is only shown when this function is set.
   */
  selectFunction?: (version: WorkerVersion) => unknown | Promise<unknown>
  /**
   * When a `selectFunction` is defined, only a WorkerVersion with an `available` state will be selectable.
   */
  onlySelectAvailable?: boolean
}>()

const loading = ref(false)

const notificationStore = useNotificationStore()

const isSelectable = computed<boolean>(() => {
  // The button is not supposed to be displayed at all
  if (!selectFunction) return false
  // On processes, only allow selecting available versions
  if (onlySelectAvailable) return version.state === 'available'
  // Outside of the context of any process, anything can be selected
  return true
})

const selectTitle = computed<string | undefined>(() => {
  if (!selectFunction) return
  else if (onlySelectAvailable && !isSelectable.value) return 'This version is not available'
  return 'Select this worker version'
})

async function select() {
  if (!isSelectable.value || !selectFunction || loading.value) return
  loading.value = true
  try {
    await selectFunction(version)
  } catch (err) {
    notificationStore.notify({ type: 'error', text: errorParser(err) })
  } finally {
    loading.value = false
  }
}
</script>
